import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Plus } from 'lucide-react';
import { motion, useSpring, useMotionValue, useTransform } from 'framer-motion';
import GoldRule from '../components/GoldRule';
import useProducts from '../hooks/useProducts';
import useProductImages, { cleanImage } from '../hooks/useProductImages';
import { resolveSetItem, getSetHero } from '../data/products';
import { getPhaseOffset } from '../lib/animPhase';
import LeatherBackdrop from '../components/LeatherBackdrop';
import { Reveal, RevealLines, Stagger, StaggerItem } from '../components/motion/Reveal';

export default function Sets() {
  const { sets: SETS_MERGED, products } = useProducts();
  const { manifest } = useProductImages();
  const flagship = SETS_MERGED.find((s) => s.featured);
  const custom = SETS_MERGED.find((s) => s.custom);
  const standard = SETS_MERGED.filter((s) => !s.featured && !s.custom);

  return (
    <div data-testid="sets-page">
      <section className="relative overflow-hidden">
        <LeatherBackdrop variant="grain" intensity={0.34} drift={9} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <Reveal y={12}>
            <div className="eyebrow text-mb-muted mb-8" data-testid="sets-eyebrow">
              The collection
            </div>
          </Reveal>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-mb leading-[0.95]">
            <RevealLines lines={['Three sets.']} />
            <RevealLines lines={['Or build your own.']} delay={0.1} lineClassName="italic" />
          </h1>
          <Reveal delay={0.3} className="mt-10">
            <GoldRule size="med" />
          </Reveal>
          <Reveal delay={0.36}>
            <p className="mt-10 font-display italic text-[18px] md:text-[20px] text-mb-muted max-w-2xl leading-relaxed">
              The Briefing for the working week, The Corner Office for the
              promotion, The Legacy for the gesture that is not forgotten — and
              My Mr. Boss, assembled piece by piece by you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE FULL HOUSE — entire catalogue composed in one shot */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16" data-testid="sets-full-catalogue">
        <Reveal>
          <div className="relative aspect-[16/9] overflow-hidden hairline border-gold bg-mb">
            <img
              src={getSetHero('full-catalogue')}
              alt="The full MR. BOSS & CO. house collection"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(8,9,10,0.85) 0%, transparent 45%)' }}
            />
            <div className="absolute left-6 md:left-10 bottom-6 md:bottom-8">
              <div className="eyebrow text-gold mb-2">The house, at a glance</div>
              <div className="font-display italic text-xl md:text-3xl text-mb">
                Every piece we make, in one frame.
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16">
        <Stagger className="grid md:grid-cols-2 gap-8" gap={0.11}>
          {standard.map((set) => (
            <StaggerItem key={set.slug} className="h-full">
              <SetCard set={set} products={products} manifest={manifest} />
            </StaggerItem>
          ))}
        </Stagger>

        {flagship && (
          <Reveal delay={0.08} className="mt-8">
            <FlagshipCard set={flagship} products={products} manifest={manifest} />
          </Reveal>
        )}

        {custom && (
          <Reveal delay={0.1} className="mt-8">
            <CustomCard set={custom} />
          </Reveal>
        )}
      </section>

      <section className="hairline-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="eyebrow text-mb-muted mb-6">In quantity</div>
          <h2 className="font-display italic text-3xl md:text-4xl text-mb">
            Gifting a room, not a person?
          </h2>
          <p className="mt-8 text-[15px] text-mb-muted max-w-lg mx-auto">
            Open any set below — every set page carries an enquiry option at
            the bottom for custom leathers, embossing and multi-tier distribution.
          </p>
        </div>
      </section>
    </div>
  );
}

// ── 3D tilt hook ─────────────────────────────────────────────
function useTilt(strength = 7) {
  const ref = useRef(null);
  const rawX = useMotionValue(0.5);
  const rawY = useMotionValue(0.5);
  const rotateY = useSpring(useTransform(rawX, [0, 1], [-strength, strength]), { stiffness: 55, damping: 18 });
  const rotateX = useSpring(useTransform(rawY, [0, 1], [strength * 0.7, -strength * 0.7]), { stiffness: 55, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    rawX.set((e.clientX - r.left) / r.width);
    rawY.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { rawX.set(0.5); rawY.set(0.5); };
  return { ref, rotateX, rotateY, onMove, onLeave };
}

// ── Cover image (single or cycling) ──────────────────────────
const Cover = ({ set, products, manifest, ratio = 'aspect-[5/4]', className = '' }) => {
  // Collect up to 4 styled images from set pieces for cycling
  const images = set.items
    .slice(0, 4)
    .map((entry) => {
      const piece = resolveSetItem(entry, products);
      return cleanImage(manifest, piece.code || entry);
    })
    .filter(Boolean);

  // Desync each set's cycle from every other set's cycle — without this,
  // cards with the same image count all flip in lock-step (same wall-clock
  // start + identical duration/delay pattern), which looked repetitive.
  const cycleDuration = images.length * 2;
  const phaseOffset = getPhaseOffset(set.slug, cycleDuration);

  return (
    <div className={`relative ${ratio} overflow-hidden bg-mb hairline border-subtle group-hover:border-gold/40 transition-colors duration-500 ${className}`}>
      {images.map((src, idx) => (
        <img
          key={src || idx}
          src={src}
          alt={`${set.name} piece ${idx + 1}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          style={{
            opacity: idx === 0 ? 1 : 0,
            animation: images.length > 1
              ? `set-img-cycle ${images.length * 2}s ease-in-out infinite ${idx * 2 + phaseOffset}s`
              : 'none',
          }}
          onError={(e) => { e.currentTarget.style.opacity = '0'; }}
        />
      ))}
      {/* Piece count badge */}
      <motion.div
        className="absolute left-3 bottom-3 eyebrow-sm text-gold bg-mb/80 px-2 py-1"
        initial={{ opacity: 0.7 }}
        whileHover={{ opacity: 1 }}
      >
        {set.items.length} pieces inside
      </motion.div>
      {/* Bottom gold rule reveal on hover */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[1px] bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out"
      />
    </div>
  );
};

// ── Standard set card (2-up grid) ────────────────────────────
const SetCard = ({ set, products, manifest }) => {
  const { ref, rotateX, rotateY, onMove, onLeave } = useTilt(5);

  return (
    <motion.div
      ref={ref}
      className="h-full"
      style={{ perspective: '900px' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <Link
        to={`/sets/${set.slug}`}
        data-testid={`set-card-${set.slug}`}
        className="relative block h-full bg-mb-surface hairline border-subtle hover:border-gold transition-colors duration-300 group"
      >
        <motion.div
          className="h-full"
          style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        >
          <div className="p-8 flex items-start justify-between">
            <div className="eyebrow-sm text-gold">{set.leather}</div>
            <div className="text-right">
              {set.price ? (
                <div className="font-display text-lg text-gold" data-testid={`set-card-price-${set.slug}`}>
                  £{Number(set.price).toFixed(2)}
                </div>
              ) : null}
              <div className="eyebrow-sm text-mb-muted">{set.pieceCount} pcs</div>
            </div>
          </div>

          <div className="px-8">
            <Cover set={set} products={products} manifest={manifest} />
          </div>

          <div className="p-8">
            <h3 className="font-display text-3xl text-mb group-hover:text-gold transition-colors duration-300">
              {set.name}
            </h3>
            <p className="mt-3 font-display italic text-[15px] text-mb-muted">
              {set.tagline}
            </p>
            <ul className="mt-6 space-y-2">
              {set.items.slice(0, 4).map((entry, i) => {
                const piece = resolveSetItem(entry, products);
                return (
                  <motion.li
                    key={entry}
                    className="eyebrow-sm text-mb-muted"
                    initial={{ opacity: 0, x: -6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {piece.name}
                    {piece.pending && <span className="text-gold"> ·</span>}
                  </motion.li>
                );
              })}
              {set.items.length > 4 && (
                <li className="eyebrow-sm text-gold">+{set.items.length - 4} more</li>
              )}
            </ul>
            <div className="mt-8 hairline-t border-subtle pt-5 flex items-center justify-between">
              <div className="eyebrow-sm text-mb-muted">
                {set.laptop ? `${set.laptop} laptop` : 'By order'}
              </div>
              <motion.span
                className="inline-flex items-center gap-2 eyebrow text-gold"
                whileHover={{ x: 3 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                Open <ArrowUpRight size={12} strokeWidth={1.25} />
              </motion.span>
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

// ── Flagship card (full-width feature) ───────────────────────
const FlagshipCard = ({ set, products, manifest }) => {
  const imgX = useMotionValue(0);
  const imgY = useMotionValue(0);
  const imgRef = useRef(null);

  const onImgMove = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width - 0.5) * 10;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 10;
    imgX.set(x);
    imgY.set(y);
  };
  const onImgLeave = () => { imgX.set(0); imgY.set(0); };
  const smoothX = useSpring(imgX, { stiffness: 60, damping: 18 });
  const smoothY = useSpring(imgY, { stiffness: 60, damping: 18 });

  return (
    <Link
      to={`/sets/${set.slug}`}
      data-testid={`set-card-${set.slug}`}
      className="block hairline border-gold bg-mb-surface hover:bg-mb-rich transition-colors duration-300 group"
    >
      <div className="grid md:grid-cols-12 gap-10 p-8 md:p-12">
        <div className="md:col-span-5 flex flex-col">
          <div className="flex items-center gap-4">
            <div className="eyebrow text-gold">The house at full measure</div>
            <div className="eyebrow-sm text-mb-muted">{set.pieceCount} pcs</div>
          </div>
          <h3 className="mt-10 font-display text-4xl md:text-6xl text-mb leading-[0.95] group-hover:text-gold transition-colors duration-300">
            {set.name}
          </h3>
          <div className="mt-6">
            <GoldRule size="med" />
          </div>
          <p className="mt-6 font-display italic text-[18px] md:text-[20px] text-mb-muted">
            {set.tagline}
          </p>
          <p className="mt-6 text-[14px] text-mb-muted leading-relaxed max-w-md">
            {set.description}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <span className="feature-badge">{set.leather}</span>
            {set.laptop && <span className="feature-badge">{set.laptop} laptop</span>}
          </div>
          <div className="mt-10 inline-flex items-center gap-3">
            <motion.span
              className="btn-gold"
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              Open the set
            </motion.span>
            <span className="eyebrow text-mb-muted" data-testid={`set-card-price-${set.slug}`}>
              {set.price ? `£${Number(set.price).toFixed(2)}` : 'By order'}
            </span>
          </div>
        </div>

        <div className="md:col-span-7">
          {/* Hero image with parallax */}
          <div
            ref={imgRef}
            className="relative overflow-hidden border-gold hairline"
            onMouseMove={onImgMove}
            onMouseLeave={onImgLeave}
          >
            <div className={`relative aspect-[16/10]`}>
              <motion.img
                src={getSetHero(set.slug)}
                alt={set.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ x: smoothX, y: smoothY, scale: 1.06 }}
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
            </div>
          </div>

          {/* Thumbnail strip — hover to highlight */}
          <div className="mt-6 grid grid-cols-4 gap-3">
            {set.items
              .filter((entry) => !resolveSetItem(entry, products).pending)
              .slice(1, 5)
              .map((code, i) => {
                const img = cleanImage(manifest, code);
                if (!img) return null;
                return (
                  <motion.div
                    key={code}
                    className="relative aspect-square overflow-hidden bg-mb hairline border-subtle"
                    whileHover={{ scale: 1.04, borderColor: 'rgba(210,177,96,0.7)' }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    style={{ transitionProperty: 'border-color' }}
                  >
                    <img
                      src={img}
                      alt={code}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                  </motion.div>
                );
              })}
          </div>
        </div>
      </div>
    </Link>
  );
};

// ── Custom "build your own" card ──────────────────────────────
const CustomCard = ({ set }) => (
  <Link
    to={`/sets/${set.slug}`}
    data-testid={`set-card-${set.slug}`}
    className="block hairline border-subtle bg-mb hover:border-gold transition-colors duration-300 group"
  >
    <div className="grid md:grid-cols-12 gap-10 p-8 md:p-12 items-center">
      <div className="md:col-span-8">
        <div className="eyebrow text-gold">Build your own</div>
        <h3 className="mt-8 font-display text-4xl md:text-5xl text-mb leading-[1] group-hover:text-gold transition-colors duration-300">
          {set.name}
        </h3>
        <p className="mt-6 font-display italic text-[18px] text-mb-muted">
          {set.tagline}
        </p>
        <p className="mt-6 text-[14px] text-mb-muted leading-relaxed max-w-xl">
          {set.description}
        </p>
        <div className="mt-10 inline-flex items-center gap-3">
          <span className="btn-outline-gold">Start building</span>
          <span className="eyebrow text-mb-muted">Three, four, seven — or free hand</span>
        </div>
      </div>
      <div className="md:col-span-4 hidden md:flex justify-end">
        <div className="w-[150px] h-[150px] hairline border-gold flex items-center justify-center group-hover:bg-mb-surface transition-colors duration-300">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          >
            <Plus size={40} strokeWidth={0.6} className="text-gold" />
          </motion.div>
        </div>
      </div>
    </div>
  </Link>
);
